import { z } from "zod";

export const UUIDSchema = z.string().uuid();
export const IsoTimestampSchema = z.string().datetime({ offset: true });
export const IanaTimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine((timeZone) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone }).format(0);
      return true;
    } catch {
      return false;
    }
  }, "Invalid IANA timezone");
export const ReferenceIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .regex(/^[\p{L}\p{N}][\p{L}\p{N}._:/-]*$/u, "Invalid reference identifier");

export const ShortTextSchema = z.string().trim().max(240);
export const RequiredShortTextSchema = ShortTextSchema.min(1);
export const UserTextSchema = z.string().max(100_000);
export const OptionalUserTextSchema = UserTextSchema.optional();
export const SortOrderSchema = z.number().finite().min(-1_000_000).max(1_000_000);

export const TimestampedFieldsSchema = z.object({
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});

export const SoftDeleteFieldsSchema = z.object({
  deletedAt: IsoTimestampSchema.optional(),
});

export type UUID = z.infer<typeof UUIDSchema>;
export type IsoTimestamp = z.infer<typeof IsoTimestampSchema>;

export function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

export function createUuid(): UUID {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID !== undefined) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues === undefined) {
    throw new Error("A cryptographically secure UUID generator is unavailable.");
  }

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] ?? 0) & 0x0f | 0x40;
  bytes[8] = (bytes[8] ?? 0) & 0x3f | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return UUIDSchema.parse(
    `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`,
  );
}

export function normalizeLabel(value: string): string {
  // Locale-independent casing keeps indexes and portable backups deterministic
  // across browsers configured for different interface locales.
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
}

export function hasUniqueValues(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

export function optionalNonEmpty<T extends z.ZodType<string>>(schema: T) {
  return schema.optional();
}
